import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, } from 'react';
import { authService, } from '../services/core/authService';
import API_BASE_URL from '../config/api';
import { useOptimizedStorage } from '../hooks/useOptimizedStorage';
import { devLogger } from '../utils/devLogger';
import { inicializarPushNotifications, removerPushSubscription, registrarListenerSomSW, } from '../services/pushNotifications';
import { refreshTokenStorage } from '../services/core/refreshTokenStorage';
import { clearSharedAccessToken } from '../services/core/tokenRefreshFlow';
import { clearWebSessionMarker, markWebSessionPresent, WEB_SESSION_MARKER, } from '../services/core/authSession';
import { isSuccessfulWebSessionRefresh, refreshWebSession, } from '../services/core/webSessionRefresh';
import { LOCAL_STORAGE_KEYS } from '../utils/constants/appConstants';
const mapApiUserToUserData = (apiUser) => {
    const profileName = String(apiUser.perfil_nome ?? apiUser.perfil?.nome ?? apiUser.perfil?.name ?? '');
    const permissions = Array.isArray(apiUser.permissoes) ? apiUser.permissoes : [];
    return {
        id: Number(apiUser.id),
        username: String(apiUser.username ?? ''),
        nome_completo: String(apiUser.nome_completo ?? apiUser.nome ?? apiUser.username ?? ''),
        email: apiUser.email,
        setor_nome: apiUser.setor_nome,
        perfil_nome: profileName,
        perfil: {
            id: Number(apiUser.perfil_id ?? apiUser.perfil?.id ?? 0),
            nome: profileName,
        },
        permissoes: permissions.map((permission, index) => ({
            id: Number(permission.id ?? index),
            modulo: String(permission.modulo ?? ''),
            acao: String(permission.acao ?? ''),
            descricao: permission.descricao,
        })),
        setores_permissao_hora_extra: apiUser.setores_permissao_hora_extra ?? [],
        colaborador_id: apiUser.colaborador_id ?? null,
        colaborador_nome: apiUser.colaborador_nome ?? null,
        ativo: apiUser.ativo !== false,
        created_at: apiUser.created_at ?? new Date().toISOString(),
        updated_at: apiUser.updated_at,
        aviso_boas_vindas_confirmado_em: apiUser.aviso_boas_vindas_confirmado_em ?? null,
        deve_trocar_senha: apiUser.deve_trocar_senha ?? false,
    };
};
const clearLocalStoragePreservingKeys = (keysToPreserve) => {
    if (typeof window === 'undefined') {
        return;
    }
    const preservedEntries = keysToPreserve
        .map(key => [key, localStorage.getItem(key)])
        .filter(([, value]) => value !== null);
    localStorage.clear();
    preservedEntries.forEach(([key, value]) => {
        if (value !== null) {
            localStorage.setItem(key, value);
        }
    });
};
const AUTH_ACTIVITY_STORAGE_KEY = 'icavi:auth-activity';
const ACTIVITY_BROADCAST_INTERVAL = 5 * 1000;
const readSharedActivityTimestamp = () => {
    if (typeof window === 'undefined') {
        return 0;
    }
    try {
        const timestamp = Number(localStorage.getItem(AUTH_ACTIVITY_STORAGE_KEY));
        return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
    }
    catch {
        return 0;
    }
};
const serializeUserSnapshot = (user) => {
    try {
        if (!user) {
            return null;
        }
        const snapshot = { ...user };
        delete snapshot.created_at;
        delete snapshot.updated_at;
        return JSON.stringify(snapshot);
    }
    catch (error) {
        devLogger.warn('Não foi possível serializar userData para snapshot:', error);
        return null;
    }
};
const normalizeUserFromStorage = (user) => {
    if (!user) {
        return user;
    }
    const normalizedUser = { ...user };
    if (!normalizedUser.access_token && normalizedUser.token) {
        normalizedUser.access_token = normalizedUser.token;
    }
    if (!normalizedUser.token && normalizedUser.access_token) {
        normalizedUser.token = normalizedUser.access_token;
    }
    return normalizedUser;
};
const usuariosSaoIguais = (atual, proximo) => {
    if (atual === proximo) {
        return true;
    }
    if (!atual || !proximo) {
        return false;
    }
    const camposBase = [
        'id',
        'username',
        'nome_completo',
        'perfil_nome',
        'token',
        'access_token',
        'refresh_token',
        'perfil',
        'colaborador_id',
        'colaborador_nome',
    ];
    const camposIguais = camposBase.every(campo => {
        const valorAtual = atual[campo];
        const valorProximo = proximo[campo];
        if (typeof valorAtual === 'object' && valorAtual !== null) {
            return JSON.stringify(valorAtual) === JSON.stringify(valorProximo);
        }
        return valorAtual === valorProximo;
    });
    if (!camposIguais) {
        return false;
    }
    const permissoesAtuais = Array.isArray(atual.permissoes) ? atual.permissoes : [];
    const permissoesNovas = Array.isArray(proximo.permissoes) ? proximo.permissoes : [];
    if (permissoesAtuais.length !== permissoesNovas.length) {
        return false;
    }
    return permissoesAtuais.every((permissao, index) => {
        const destino = permissoesNovas[index];
        return permissao?.modulo === destino?.modulo && permissao?.acao === destino?.acao;
    });
};
const isRefreshTokenResponse = (response) => {
    if (!response || typeof response !== 'object') {
        return false;
    }
    return 'access_token' in response || 'token' in response || 'refresh_token' in response;
};
const isTemporaryAuthResponse = (value) => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const response = value;
    return response.codigo === 'AUTH_UNAVAILABLE' || Number(response.http_status) >= 500;
};
const isTemporaryAuthError = (value) => {
    if (!value || typeof value !== 'object') {
        return true;
    }
    const error = value;
    return !error.response || Number(error.response.status) >= 500;
};
// ============================================================================
// Context Creation
// ============================================================================
export const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};
// ============================================================================
// AuthProvider Component
// ============================================================================
export const AuthProvider = ({ children }) => {
    // Hook para operações otimizadas de localStorage
    const { removeMultiple: removeStorageItems, setMultiple: setStorageItems } = useOptimizedStorage();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [pendingAuthContext, setPendingAuthContext] = useState(null);
    const [pendingRecoveryCodes, setPendingRecoveryCodes] = useState([]);
    // Novos estados para funcionalidades avançadas
    const [sessionExpiry, setSessionExpiry] = useState(null);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const permissionsCacheRef = useRef(new Map());
    const [refreshInProgress, setRefreshInProgress] = useState(false);
    const authChannelRef = useRef(null);
    const tabIdRef = useRef(typeof crypto !== 'undefined' && crypto?.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    // Refs para controle de timers e funções para evitar dependências circulares
    const refreshTimer = useRef(null);
    const activityTimer = useRef(null);
    const sessionWarningTimer = useRef(null);
    const refreshUserDataRef = useRef(null);
    const lastHydratedUserRef = useRef(null);
    const refreshTokenRef = useRef(null);
    const authOperationVersionRef = useRef(0);
    const logoutInProgressRef = useRef(false);
    const permissionsFingerprintRef = useRef('');
    const lastActivityRef = useRef(Date.now());
    const lastActivityBroadcastRef = useRef(0);
    // Controle para limitar disparos de atividade e evitar loops de setState
    const activityThrottleRef = useRef(Date.now());
    // Configurações
    const SESSION_WARNING_TIME = 5 * 60 * 1000; // 5 minutos antes de expirar
    // Padrão até o backend informar session_inactivity_seconds (AUTH_INACTIVITY_SECONDS no .env).
    const DEFAULT_ACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000;
    // Limite do setTimeout (~24,8 dias); acima disso o timer dispararia na hora.
    const MAX_TIMER_DELAY = 2147483647;
    const activityTimeoutRef = useRef(DEFAULT_ACTIVITY_TIMEOUT);
    // Alinha o timer local ao prazo de inatividade configurado no backend.
    const applySessionInactivity = useCallback((seconds) => {
        if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
            return;
        }
        const timeoutMs = seconds * 1000;
        if (timeoutMs === activityTimeoutRef.current) {
            return;
        }
        activityTimeoutRef.current = timeoutMs;
        updateActivityRef.current();
    }, []);
    const PERMISSION_CACHE_TTL = 60 * 1000; // 1 minuto
    const DEFAULT_ACCESS_TOKEN_TTL = 60 * 60; // 1 hora (fallback)
    const AUTH_TOKEN_UPDATED_EVENT = 'icavi:auth-token-updated';
    const AUTH_FORCE_LOGOUT_EVENT = 'icavi:auth-force-logout';
    const createPermissionsFingerprint = useCallback((targetUser) => {
        if (!targetUser) {
            return '';
        }
        const permissoes = Array.isArray(targetUser.permissoes)
            ? targetUser.permissoes
                .map(permissao => `${String(permissao.modulo || '')
                .trim()
                .toLowerCase()}:${String(permissao.acao || '')
                .trim()
                .toLowerCase()}`)
                .sort()
            : [];
        return `${targetUser.id}|${targetUser.perfil?.nome || targetUser.perfil_nome || ''}|${permissoes.join(',')}`;
    }, []);
    const invalidatePermissionsCache = useCallback((targetUser) => {
        const nextFingerprint = createPermissionsFingerprint(targetUser);
        if (permissionsFingerprintRef.current !== nextFingerprint ||
            permissionsCacheRef.current.size > 0) {
            permissionsCacheRef.current = new Map();
        }
        permissionsFingerprintRef.current = nextFingerprint;
    }, [createPermissionsFingerprint]);
    const storeRefreshToken = useCallback((token) => {
        const safeToken = token?.trim();
        if (!safeToken) {
            refreshTokenRef.current = null;
            refreshTokenStorage.clear();
            return;
        }
        refreshTokenRef.current = safeToken;
        refreshTokenStorage.set(safeToken);
    }, []);
    const getStoredRefreshToken = useCallback(() => {
        if (refreshTokenRef.current) {
            return refreshTokenRef.current;
        }
        const storedToken = refreshTokenStorage.get();
        if (storedToken) {
            refreshTokenRef.current = storedToken;
            return storedToken;
        }
        return null;
    }, []);
    const broadcastAuthEvent = useCallback((payload) => {
        if (authChannelRef.current && payload) {
            authChannelRef.current.postMessage({
                source: tabIdRef.current,
                ...payload,
            });
        }
    }, []);
    const broadcastActivity = useCallback((timestamp) => {
        if (timestamp - lastActivityBroadcastRef.current < ACTIVITY_BROADCAST_INTERVAL) {
            return;
        }
        lastActivityBroadcastRef.current = timestamp;
        broadcastAuthEvent({ type: 'ACTIVITY', timestamp });
        try {
            localStorage.setItem(AUTH_ACTIVITY_STORAGE_KEY, String(timestamp));
        }
        catch {
            // O canal BroadcastChannel continua disponível quando o storage é bloqueado.
        }
    }, [broadcastAuthEvent]);
    const updateSessionTimers = useCallback((expiresIn) => {
        if (sessionWarningTimer.current) {
            clearTimeout(sessionWarningTimer.current);
            sessionWarningTimer.current = null;
        }
        if (!expiresIn || expiresIn <= 0) {
            setSessionExpiry(null);
            return;
        }
        const expiryTime = Date.now() + expiresIn * 1000;
        setSessionExpiry(expiryTime);
        const warningTime = expiryTime - SESSION_WARNING_TIME;
        const timeUntilWarning = warningTime - Date.now();
        if (timeUntilWarning > 0) {
            sessionWarningTimer.current = setTimeout(() => {
                devLogger.log('[AVISO] Sessao expira em breve');
            }, timeUntilWarning);
        }
    }, [SESSION_WARNING_TIME]);
    const persistAuthState = useCallback((updatedUser, options = {}) => {
        const { expiresIn, broadcast = true } = options;
        const nextUser = {
            ...updatedUser,
        };
        // A prova Web fica exclusivamente nos cookies. Nunca replique access ou
        // refresh tokens no estado React ou no localStorage.
        delete nextUser.token;
        delete nextUser.access_token;
        delete nextUser.refresh_token;
        storeRefreshToken();
        const persistedUser = {
            ...nextUser,
        };
        delete persistedUser.refresh_token;
        const storagePayload = {
            userData: persistedUser,
        };
        markWebSessionPresent();
        setStorageItems(storagePayload, true);
        invalidatePermissionsCache(nextUser);
        setUser(prev => (usuariosSaoIguais(prev, nextUser) ? prev : nextUser));
        setIsAuthenticated(prev => (prev ? prev : true));
        lastHydratedUserRef.current = serializeUserSnapshot(persistedUser);
        if (expiresIn) {
            updateSessionTimers(expiresIn);
        }
        if (broadcast) {
            broadcastAuthEvent({ type: 'USER_UPDATED' });
        }
        return nextUser;
    }, [
        setStorageItems,
        broadcastAuthEvent,
        updateSessionTimers,
        invalidatePermissionsCache,
        storeRefreshToken,
    ]);
    // Função para atualizar dados do usuário do servidor (usando ref para evitar dependência circular)
    const refreshUserData = useCallback(async () => {
        if (refreshInProgress) {
            return false;
        }
        try {
            setRefreshInProgress(true);
            const refreshResponse = await refreshWebSession();
            if (!isSuccessfulWebSessionRefresh(refreshResponse)) {
                return false;
            }
            const expiresIn = 'expires_in' in refreshResponse
                ? (refreshResponse.expires_in ?? DEFAULT_ACCESS_TOKEN_TTL)
                : DEFAULT_ACCESS_TOKEN_TTL;
            const currentUserResponse = await authService.getCurrentUser();
            if (!currentUserResponse || !('user' in currentUserResponse) || !currentUserResponse.user) {
                return false;
            }
            persistAuthState(mapApiUserToUserData(currentUserResponse.user), {
                expiresIn,
                broadcast: false,
            });
            applySessionInactivity(currentUserResponse.session_inactivity_seconds);
            broadcastAuthEvent({ type: 'USER_UPDATED' });
            return true;
        }
        catch (error) {
            console.error('Erro ao atualizar dados do usuario:', error);
            return false;
        }
        finally {
            setRefreshInProgress(false);
        }
    }, [
        applySessionInactivity,
        authService,
        broadcastAuthEvent,
        persistAuthState,
        refreshInProgress,
    ]);
    // Atualizar ref sempre que refreshUserData mudar
    useEffect(() => {
        refreshUserDataRef.current = refreshUserData;
    }, [refreshUserData]);
    // WebSocket: escutar evento permissions_changed para refresh imediato
    useEffect(() => {
        if (!isAuthenticated || !user)
            return;
        let cleanupPermissions = null;
        let cleanupAvisos = null;
        const connectSocket = async () => {
            try {
                const { addPortalSocketListener } = await import('../services/core/portalSocket');
                cleanupPermissions = addPortalSocketListener('permissions_changed', () => {
                    devLogger.log('[Auth] Permissões alteradas pelo admin — forçando refresh');
                    if (refreshUserDataRef.current) {
                        void refreshUserDataRef.current();
                    }
                });
                cleanupAvisos = addPortalSocketListener('aviso_sistema', (...args) => {
                    const payload = args[0];
                    if (payload?.aviso?.id) {
                        window.dispatchEvent(new CustomEvent('portal:aviso_sistema', { detail: payload.aviso }));
                    }
                });
            }
            catch {
                // socket.io-client indisponível — fallback silencioso para polling
            }
        };
        void connectSocket();
        return () => {
            cleanupPermissions?.();
            cleanupAvisos?.();
        };
    }, [isAuthenticated, user?.id]);
    // A sessão Web usa cookies opacos; atividade real nas rotas protegidas renova
    // o prazo de inatividade no backend. Não há refresh periódico em background.
    const setupTokenRefresh = useCallback((tokenData) => {
        if (refreshTimer.current) {
            clearTimeout(refreshTimer.current);
            refreshTimer.current = null;
        }
        updateSessionTimers(tokenData?.expires_in ?? DEFAULT_ACCESS_TOKEN_TTL);
    }, [updateSessionTimers]);
    // Função para limpar todos os timers
    const clearAllTimers = useCallback(() => {
        if (refreshTimer.current) {
            clearInterval(refreshTimer.current);
            refreshTimer.current = null;
        }
        if (activityTimer.current) {
            clearTimeout(activityTimer.current);
            activityTimer.current = null;
        }
        if (sessionWarningTimer.current) {
            clearTimeout(sessionWarningTimer.current);
            sessionWarningTimer.current = null;
        }
    }, []);
    const applyLogoutState = useCallback(() => {
        clearAllTimers();
        clearWebSessionMarker();
        removeStorageItems(['userData', 'token'], true);
        try {
            localStorage.removeItem(AUTH_ACTIVITY_STORAGE_KEY);
        }
        catch {
            // O estado local principal já foi limpo acima.
        }
        refreshTokenRef.current = null;
        refreshTokenStorage.clear();
        clearSharedAccessToken();
        lastHydratedUserRef.current = null;
        setUser(prev => (prev === null ? prev : null));
        setIsAuthenticated(prev => (prev ? false : prev));
        invalidatePermissionsCache(null);
        setSessionExpiry(null);
        setLastActivity(Date.now());
        setRefreshInProgress(false);
        setPendingAuthContext(null);
        setPendingRecoveryCodes([]);
        setLoading(false);
        devLogger.log('✅ Logout concluído');
    }, [clearAllTimers, invalidatePermissionsCache, removeStorageItems]);
    const checkAuthStatus = useCallback(() => {
        if (logoutInProgressRef.current) {
            return;
        }
        const operationVersion = authOperationVersionRef.current;
        setLoading(true);
        void (async () => {
            try {
                const response = await authService.getCurrentUser();
                if (logoutInProgressRef.current || operationVersion !== authOperationVersionRef.current) {
                    return;
                }
                if (!response || !('user' in response) || !response.user) {
                    if (isTemporaryAuthResponse(response)) {
                        devLogger.warn('Autoridade de autenticação temporariamente indisponível.');
                        return;
                    }
                    devLogger.warn('Sessão Web não pôde ser restaurada:', response);
                    applyLogoutState();
                    return;
                }
                const nextUser = mapApiUserToUserData(response.user);
                persistAuthState(nextUser, { broadcast: false });
                applySessionInactivity(response.session_inactivity_seconds);
            }
            catch (error) {
                if (logoutInProgressRef.current || operationVersion !== authOperationVersionRef.current) {
                    return;
                }
                if (isTemporaryAuthError(error)) {
                    devLogger.warn('Sessão Web não pôde ser verificada temporariamente:', error);
                    return;
                }
                devLogger.warn('Sessão Web não pôde ser restaurada:', error);
                applyLogoutState();
            }
            finally {
                if (!logoutInProgressRef.current && operationVersion === authOperationVersionRef.current) {
                    setLoading(false);
                }
            }
        })();
    }, [applyLogoutState, applySessionInactivity, authService, persistAuthState]);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
            return;
        }
        const channel = new BroadcastChannel('auth-channel');
        authChannelRef.current = channel;
        const handleMessage = (event) => {
            const data = event?.data;
            if (!data || data.source === tabIdRef.current) {
                return;
            }
            switch (data.type) {
                case 'FORCE_LOGOUT':
                    clearLocalStoragePreservingKeys([
                        LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_MOVIMENTACOES_FILTERS,
                        LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_ENTRADAS_INICIAIS,
                        LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_CARDS_INICIO,
                        LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_LANCADAS_COLUMN_WIDTHS,
                        LOCAL_STORAGE_KEYS.RNC_CHART_TYPES,
                        LOCAL_STORAGE_KEYS.RNC_RELATORIOS_LAYOUT,
                        LOCAL_STORAGE_KEYS.RNC_HIDDEN_CHARTS,
                        LOCAL_STORAGE_KEYS.RNC_CHART_DATE_FILTERS,
                        LOCAL_STORAGE_KEYS.RNC_HIDDEN_CHART_DATE_FIELDS,
                        LOCAL_STORAGE_KEYS.RNC_CHART_LABEL_WIDTHS,
                        LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_LAYOUT,
                        LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_HIDDEN_CHARTS,
                        LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_CHART_TYPES,
                        LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_CHART_DATE_FILTERS,
                        LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_HIDDEN_CHART_DATE_FIELDS,
                        LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_CHART_LABEL_WIDTHS,
                    ]);
                    applyLogoutState();
                    if (typeof window !== 'undefined' &&
                        window.location.pathname !== '/app/login' &&
                        !window.location.pathname.startsWith('/public/')) {
                        window.location.href = '/app/login';
                    }
                    break;
                case 'ACTIVITY':
                    if (isAuthenticated &&
                        typeof data.timestamp === 'number' &&
                        data.timestamp > lastActivityRef.current) {
                        updateActivityRef.current();
                    }
                    break;
                case 'LOGOUT':
                    applyLogoutState();
                    break;
                case 'LOGIN':
                case 'USER_UPDATED':
                    checkAuthStatus();
                    break;
                default:
                    break;
            }
        };
        channel.addEventListener('message', handleMessage);
        return () => {
            channel.removeEventListener('message', handleMessage);
            channel.close();
            authChannelRef.current = null;
        };
    }, [applyLogoutState, checkAuthStatus, isAuthenticated]);
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handleTokenUpdate = () => {
            checkAuthStatus();
        };
        window.addEventListener(AUTH_TOKEN_UPDATED_EVENT, handleTokenUpdate);
        return () => {
            window.removeEventListener(AUTH_TOKEN_UPDATED_EVENT, handleTokenUpdate);
        };
    }, [checkAuthStatus]);
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        updateActivityRef.current();
        if (!refreshTimer.current) {
            setupTokenRefresh({ expires_in: DEFAULT_ACCESS_TOKEN_TTL });
        }
    }, [DEFAULT_ACCESS_TOKEN_TTL, isAuthenticated, setupTokenRefresh]);
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handleStorageUpdate = (event) => {
            if (event.key === AUTH_ACTIVITY_STORAGE_KEY) {
                const timestamp = Number(event.newValue);
                if (isAuthenticated && Number.isFinite(timestamp) && timestamp > lastActivityRef.current) {
                    updateActivityRef.current();
                }
                return;
            }
            if (!event.key || (event.key !== 'userData' && event.key !== 'token')) {
                return;
            }
            if (event.key === 'userData' && event.newValue) {
                try {
                    const incomingUser = JSON.parse(event.newValue);
                    if (serializeUserSnapshot(incomingUser) === lastHydratedUserRef.current) {
                        return;
                    }
                }
                catch {
                    // Dados inválidos continuam seguindo para a verificação autoritativa.
                }
            }
            checkAuthStatus();
        };
        window.addEventListener('storage', handleStorageUpdate);
        return () => {
            window.removeEventListener('storage', handleStorageUpdate);
        };
    }, [checkAuthStatus, isAuthenticated]);
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handleForceLogout = () => {
            applyLogoutState();
            broadcastAuthEvent({ type: 'FORCE_LOGOUT' });
            if (window.location.pathname !== '/app/login' &&
                !window.location.pathname.startsWith('/public/')) {
                window.location.href = '/app/login';
            }
        };
        window.addEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForceLogout);
        return () => {
            window.removeEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForceLogout);
        };
    }, [applyLogoutState, broadcastAuthEvent, AUTH_FORCE_LOGOUT_EVENT]);
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        const attemptRefresh = () => {
            if (refreshInProgress) {
                return;
            }
            const timeRemaining = sessionExpiry ? sessionExpiry - Date.now() : null;
            if (timeRemaining === null || timeRemaining <= SESSION_WARNING_TIME) {
                if (refreshUserDataRef.current) {
                    void refreshUserDataRef.current();
                }
            }
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                attemptRefresh();
            }
        };
        const handleWindowFocus = () => {
            attemptRefresh();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleWindowFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [isAuthenticated, sessionExpiry, refreshInProgress, SESSION_WARNING_TIME]);
    useEffect(() => {
        // Limpar localStorage corrompido na inicialização
        const userData = localStorage.getItem('userData');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                // Verificar se tem estrutura básica válida (não limpar se só não tem permissões)
                if (!parsedUser.username || !parsedUser.perfil) {
                    devLogger.log('🧹 Limpando localStorage com estrutura inválida...');
                    localStorage.removeItem('userData');
                }
            }
            catch (error) {
                devLogger.log('🧹 Limpando localStorage inválido...');
                localStorage.removeItem('userData');
            }
        }
        checkAuthStatus();
    }, [checkAuthStatus]);
    const applyAuthResponse = useCallback(async (response) => {
        if (response.status === 'success' || response.status === 'sucesso' || response.sucesso) {
            const currentUserResponse = response.user
                ? { user: response.user }
                : await authService.getCurrentUser();
            if (!('user' in currentUserResponse) || !currentUserResponse.user) {
                return { success: false, message: 'Sessão criada sem dados do usuário.' };
            }
            const userData = mapApiUserToUserData(currentUserResponse.user);
            persistAuthState(userData, {
                expiresIn: response.expires_in ?? DEFAULT_ACCESS_TOKEN_TTL,
                broadcast: false,
            });
            setupTokenRefresh({ expires_in: response.expires_in ?? DEFAULT_ACCESS_TOKEN_TTL });
            applySessionInactivity(response.session_inactivity_seconds);
            updateActivityRef.current();
            setPendingAuthContext(null);
            setPendingRecoveryCodes(response.recovery_codes ?? []);
            broadcastAuthEvent({ type: 'LOGIN' });
            broadcastAuthEvent({ type: 'USER_UPDATED' });
            return { success: true, recoveryCodes: response.recovery_codes };
        }
        if (response.context_kind === 'password_change' ||
            response.context_kind === 'mfa_enroll' ||
            response.context_kind === 'mfa_challenge') {
            setPendingAuthContext({
                kind: response.context_kind,
                message: response.mensagem ?? response.message,
                mfaMethod: response.mfa_method,
                mfaMethods: response.mfa_methods,
                mfaEmail: response.mfa_email ?? response.user?.email,
                mfaEmailHint: response.mfa_email_hint,
            });
        }
        return {
            success: false,
            message: response.mensagem ??
                response.erro ??
                response.message ??
                'Autenticação adicional necessária.',
        };
    }, [applySessionInactivity, broadcastAuthEvent, persistAuthState, setupTokenRefresh]);
    const login = useCallback(async (credentials) => {
        try {
            const response = (await authService.login(credentials));
            return await applyAuthResponse(response);
        }
        catch (error) {
            console.error('Erro no login:', error);
            return { success: false, message: 'Erro interno do servidor' };
        }
    }, [applyAuthResponse]);
    const startMfaEnrollment = useCallback(async (method, secret, email) => {
        const response = (await authService.startMfaEnrollment(method, secret, email));
        setPendingAuthContext(current => current
            ? {
                ...current,
                mfaMethod: response.mfa_method ?? method,
                mfaMethods: response.mfa_methods ?? current.mfaMethods,
                mfaEmail: response.mfa_email ?? (method === 'email' ? current.mfaEmail : undefined),
                mfaEmailHint: response.mfa_email_hint ?? current.mfaEmailHint,
            }
            : current);
        return {
            success: response.status === 'mfa_enrollment_pending',
            message: response.mensagem ?? response.message,
        };
    }, []);
    const completeMfaEnrollment = useCallback(async (code) => applyAuthResponse((await authService.completeMfaEnrollment(code))), [applyAuthResponse]);
    const completeMfaChallenge = useCallback(async (code, useRecoveryCode = false) => applyAuthResponse((await authService.completeMfaChallenge(code, useRecoveryCode))), [applyAuthResponse]);
    const completePasswordChange = useCallback(async (currentPassword, newPassword) => applyAuthResponse((await authService.completePasswordChangeContext(currentPassword, newPassword))), [applyAuthResponse]);
    const dismissRecoveryCodes = useCallback(() => {
        setPendingRecoveryCodes([]);
    }, []);
    const logout = useCallback(async () => {
        logoutInProgressRef.current = true;
        authOperationVersionRef.current += 1;
        void Promise.resolve(removerPushSubscription()).catch(() => undefined);
        // Limpa o estado local imediatamente, mas mantém a Promise do logout
        // para que o chamador possa aguardar a remoção dos cookies no navegador.
        applyLogoutState();
        broadcastAuthEvent({ type: 'LOGOUT' });
        try {
            await authService.logout();
        }
        catch {
            // A sessão local já foi encerrada; falhas de rede não devem reidratar a UI.
        }
        finally {
            logoutInProgressRef.current = false;
        }
    }, [applyLogoutState, broadcastAuthEvent]);
    // Referência para manter a versão mais recente do logout sem recriar o callback
    const logoutRef = useRef(logout);
    useEffect(() => {
        logoutRef.current = logout;
    }, [logout]);
    // Função para registrar atividade do usuário
    const updateActivity = useCallback(() => {
        const timestamp = Date.now();
        lastActivityRef.current = timestamp;
        setLastActivity(timestamp);
        if (activityTimer.current) {
            clearTimeout(activityTimer.current);
        }
        activityTimer.current = setTimeout(() => {
            if (readSharedActivityTimestamp() > lastActivityRef.current) {
                updateActivityRef.current();
                return;
            }
            logoutRef.current();
        }, Math.min(activityTimeoutRef.current, MAX_TIMER_DELAY));
    }, []);
    // Referência estável para updateActivity para evitar loop infinito
    const updateActivityRef = useRef(updateActivity);
    useEffect(() => {
        updateActivityRef.current = updateActivity;
    }, [updateActivity]);
    // Função para forçar limpeza completa e novo login
    const forceCleanLogin = useCallback(() => {
        devLogger.log('🧹 Forçando limpeza completa do sistema...');
        clearLocalStoragePreservingKeys([
            LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_MOVIMENTACOES_FILTERS,
            LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_ENTRADAS_INICIAIS,
            LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_CARDS_INICIO,
            LOCAL_STORAGE_KEYS.DESPESAS_CONFERENCIA_LANCADAS_COLUMN_WIDTHS,
            LOCAL_STORAGE_KEYS.RNC_CHART_TYPES,
            LOCAL_STORAGE_KEYS.RNC_RELATORIOS_LAYOUT,
            LOCAL_STORAGE_KEYS.RNC_HIDDEN_CHARTS,
            LOCAL_STORAGE_KEYS.RNC_CHART_DATE_FILTERS,
            LOCAL_STORAGE_KEYS.RNC_HIDDEN_CHART_DATE_FIELDS,
            LOCAL_STORAGE_KEYS.RNC_CHART_LABEL_WIDTHS,
            LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_LAYOUT,
            LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_HIDDEN_CHARTS,
            LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_CHART_TYPES,
            LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_CHART_DATE_FILTERS,
            LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_HIDDEN_CHART_DATE_FIELDS,
            LOCAL_STORAGE_KEYS.RNC_OCORRENCIAS_CHART_LABEL_WIDTHS,
        ]);
        applyLogoutState();
        broadcastAuthEvent({ type: 'FORCE_LOGOUT' });
        setLoading(false);
        Promise.resolve().then(() => {
            window.location.reload();
        });
    }, [applyLogoutState, broadcastAuthEvent]);
    const updateUser = useCallback((userData) => {
        const sanitizedUserData = {
            ...userData,
        };
        delete sanitizedUserData.token;
        delete sanitizedUserData.access_token;
        delete sanitizedUserData.refresh_token;
        const serialized = JSON.stringify(sanitizedUserData);
        localStorage.setItem('userData', serialized);
        lastHydratedUserRef.current = serializeUserSnapshot(sanitizedUserData);
        setUser(prev => (usuariosSaoIguais(prev, sanitizedUserData) ? prev : sanitizedUserData));
        invalidatePermissionsCache(sanitizedUserData);
        broadcastAuthEvent({ type: 'USER_UPDATED' });
    }, [broadcastAuthEvent, invalidatePermissionsCache]);
    // Função para diagnosticar problemas de permissões
    const diagnosticarPermissoes = useCallback(async () => {
        if (!user?.username) {
            devLogger.log('❌ DIAGNÓSTICO: Usuário não tem username');
            return;
        }
        try {
            // Buscar dados atualizados do servidor
            const response = await fetch(`${API_BASE_URL}/api/debug/user-permissions/${user.username}`);
            if (response.ok) {
                const data = await response.json();
                if (data.status === 'ok') {
                    // Dados válidos recebidos
                }
            }
        }
        catch (error) {
            console.error('❌ DIAGNÓSTICO: Erro ao diagnosticar permissões:', error);
        }
    }, [user]);
    // Função para verificar se o usuário tem uma permissão específica (com cache)
    const hasPermission = useCallback((modulo, acao) => {
        if (!user) {
            return false;
        }
        // Verificar cache primeiro (usando ref para evitar setState durante render)
        const permissionsFingerprint = permissionsFingerprintRef.current || createPermissionsFingerprint(user);
        if (permissionsFingerprintRef.current !== permissionsFingerprint) {
            invalidatePermissionsCache(user);
        }
        const cacheKey = `${permissionsFingerprint}:${modulo}.${acao}`;
        const cached = permissionsCacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < PERMISSION_CACHE_TTL) {
            return cached.result;
        }
        let result = false;
        if (!user.permissoes || !Array.isArray(user.permissoes)) {
            // Log do problema mas não tentar recarregar automaticamente para evitar setState durante render
            result = false;
        }
        else {
            // Super Admin tem todas as permissões
            if (user.perfil?.nome === 'Super Admin') {
                result = true;
            }
            else {
                // Verificar se o usuário tem a permissão específica (case-insensitive)
                const moduloNormalized = String(modulo).trim().toLowerCase();
                const acaoNormalized = String(acao).trim().toLowerCase();
                result = user.permissoes.some(permissao => {
                    const permModulo = String(permissao.modulo || '')
                        .trim()
                        .toLowerCase();
                    const permAcao = String(permissao.acao || '')
                        .trim()
                        .toLowerCase();
                    return permModulo === moduloNormalized && permAcao === acaoNormalized;
                });
            }
        }
        // Armazenar no cache usando ref (não causa re-render)
        permissionsCacheRef.current.set(cacheKey, {
            result,
            timestamp: Date.now(),
        });
        // Limpar cache antigo (manter apenas últimas 100 entradas)
        if (permissionsCacheRef.current.size > 100) {
            const entries = Array.from(permissionsCacheRef.current.entries());
            entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
            permissionsCacheRef.current = new Map(entries.slice(0, 100));
        }
        return result;
    }, [user, PERMISSION_CACHE_TTL, createPermissionsFingerprint, invalidatePermissionsCache]);
    useEffect(() => {
        invalidatePermissionsCache(user);
    }, [invalidatePermissionsCache, user]);
    // Função para verificar se o usuário pode acessar um módulo (qualquer ação)
    const canAccessModule = useCallback((modulo) => {
        if (!user || !user.permissoes) {
            devLogger.log('❌ canAccessModule: Usuário ou permissões não encontrados', { modulo });
            return false;
        }
        // Super Admin tem acesso a tudo
        if (user.perfil?.nome === 'Super Admin') {
            return true;
        }
        // Verificar se o usuário tem pelo menos uma permissão no módulo
        const podeAcessar = user.permissoes.some(permissao => permissao.modulo === modulo);
        return podeAcessar;
    }, [user]);
    const value = useMemo(() => ({
        user,
        loading,
        isAuthenticated,
        login,
        logout,
        updateUser,
        refreshUserData,
        checkAuthStatus,
        hasPermission,
        canAccessModule,
        diagnosticarPermissoes,
        forceCleanLogin,
        // Novas funcionalidades avançadas
        sessionExpiry,
        lastActivity,
        updateActivity,
        refreshInProgress,
        // Compatibilidade de presença: a credencial real permanece nos cookies.
        token: isAuthenticated ? WEB_SESSION_MARKER : undefined,
        clearPermissionsCache: () => {
            permissionsCacheRef.current = new Map();
        },
        pendingAuthContext,
        pendingRecoveryCodes,
        dismissRecoveryCodes,
        startMfaEnrollment,
        completeMfaEnrollment,
        completeMfaChallenge,
        completePasswordChange,
    }), [
        user,
        loading,
        isAuthenticated,
        login,
        logout,
        updateUser,
        refreshUserData,
        checkAuthStatus,
        hasPermission,
        canAccessModule,
        diagnosticarPermissoes,
        forceCleanLogin,
        sessionExpiry,
        lastActivity,
        updateActivity,
        refreshInProgress,
        pendingAuthContext,
        pendingRecoveryCodes,
        dismissRecoveryCodes,
        startMfaEnrollment,
        completeMfaEnrollment,
        completeMfaChallenge,
        completePasswordChange,
    ]);
    // Configurar listeners de atividade do usuário
    useEffect(() => {
        if (!isAuthenticated)
            return;
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        const handleActivity = () => {
            const now = Date.now();
            // Ignorar eventos em rajada para evitar setState encadeado (limite 500ms)
            if (now - activityThrottleRef.current < 500)
                return;
            activityThrottleRef.current = now;
            // Usar referencia estavel para evitar loop infinito
            updateActivityRef.current();
            broadcastActivity(now);
        };
        // Adicionar listeners
        activityEvents.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });
        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
        };
    }, [broadcastActivity, isAuthenticated]); // Removido updateActivity das dependências
    useEffect(() => {
        if (!isAuthenticated || !user) {
            return;
        }
        const podeReceberPush = hasPermission('aprovacao_producao', 'aprovar') ||
            hasPermission('recepcao', 'visualizar_notificacoes_fechamento');
        if (!podeReceberPush) {
            return;
        }
        // Apenas sincroniza subscription existente se permissão já foi concedida
        // Não força solicitação de permissão - isso deve ser feito explicitamente pelo usuário
        // clicando no botão "Ativar notificações" na página de configurações
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            inicializarPushNotifications({ force: false, refreshIfBackendEmpty: true }).catch(error => {
                devLogger.warn('Falha ao sincronizar push notifications:', error);
            });
        }
    }, [hasPermission, isAuthenticated, user]);
    // Registra listener para som quando push notification chega via Service Worker
    useEffect(() => {
        const cleanup = registrarListenerSomSW();
        return cleanup;
    }, []);
    // Cleanup ao desmontar componente
    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, [clearAllTimers]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
